/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  makeStyles,
  LinearProgress,
  Grid,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import { KnativeService, KnativeRevision } from '../types';

// 自定义注解 key
const BACKSTAGE_MODIFIED_BY = 'backstage.io/last-modified-by';
const BACKSTAGE_MODIFIED_AT = 'backstage.io/last-modified-at';
const BACKSTAGE_MODIFIED_ACTION = 'backstage.io/last-action';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    minHeight: 400,
    paddingTop: theme.spacing(2),
  },
  stepper: {
    paddingBottom: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  accordion: {
    marginTop: theme.spacing(2),
  },
  envTable: {
    marginTop: theme.spacing(1),
  },
  envHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  trafficTable: {
    marginTop: theme.spacing(2),
  },
  successIcon: {
    fontSize: 64,
    color: theme.palette.success.main,
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
  },
  completionBox: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  strategyCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  strategyCardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    backgroundColor: theme.palette.primary.main + '10',
  },
  strategyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  strategyChip: {
    marginLeft: theme.spacing(1),
  },
  protocolGroup: {
    flexDirection: 'row',
  },
  resourceRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  inputSmall: {
    width: 120,
  },
}));

interface EnvVar {
  name: string;
  value: string;
  // 支持 valueFrom 类型的环境变量
  valueFrom?: {
    fieldRef?: { fieldPath: string };
    secretKeyRef?: { name: string; key: string };
    configMapKeyRef?: { name: string; key: string };
  };
}


export interface CreateRevisionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  service: KnativeService | undefined;
  revisions: KnativeRevision[];
  clusterName: string;
  namespace: string;
}

const STEPS = ['基本信息', '流量策略', '创建完成'];

// 流量策略类型
type TrafficStrategy = 'no-traffic' | 'canary' | 'full';

const TRAFFIC_STRATEGIES: Array<{ value: TrafficStrategy; label: string; description: string; percent: number }> = [
  { value: 'no-traffic', label: '仅创建', description: '创建新版本但不切换流量，当前流量保持不变', percent: 0 },
  { value: 'canary', label: '灰度发布', description: '将 10% 流量切换到新版本，用于验证', percent: 10 },
  { value: 'full', label: '全量发布', description: '将 100% 流量切换到新版本', percent: 100 },
];

export const CreateRevisionDialog = ({
  open,
  onClose,
  onSuccess,
  service,
  revisions: _revisions, // eslint-disable-line @typescript-eslint/no-unused-vars
  clusterName,
  namespace,
}: CreateRevisionDialogProps): JSX.Element => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const identityApi = useApi(identityApiRef);

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: Basic info
  const [imageRegistry, setImageRegistry] = useState('');
  const [imageTag, setImageTag] = useState('');
  const [protocol, setProtocol] = useState<'http' | 'grpc'>('http');
  const [containerPort, setContainerPort] = useState('80');

  // Advanced settings
  const [concurrency, setConcurrency] = useState('0');
  const [minScale, setMinScale] = useState('0');
  const [maxScale, setMaxScale] = useState('100');
  const [cpuLimit, setCpuLimit] = useState('');
  const [memoryLimit, setMemoryLimit] = useState('');
  const [cpuRequest, setCpuRequest] = useState('');
  const [memoryRequest, setMemoryRequest] = useState('');
  const [command, setCommand] = useState('');
  const [args, setArgs] = useState('');
  const [envVars, setEnvVars] = useState<EnvVar[]>([]);

  // Step 2: Traffic strategy (simplified)
  // 默认选择 0% 流量，避免复杂的多版本流量分配问题
  const [trafficStrategy, setTrafficStrategy] = useState<TrafficStrategy>('no-traffic');

  // Step 3: Result
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  // Initialize form with current service data
  const initializeForm = () => {
    if (service) {
      const container = service.spec.template?.spec?.containers?.[0];
      if (container) {
        const fullImage = container.image || '';
        const lastColon = fullImage.lastIndexOf(':');
        if (lastColon > 0 && !fullImage.substring(lastColon).includes('/')) {
          setImageRegistry(fullImage.substring(0, lastColon));
          setImageTag(fullImage.substring(lastColon + 1));
        } else {
          setImageRegistry(fullImage);
          setImageTag('latest');
        }

        const port = container.ports?.[0]?.containerPort || 80;
        setContainerPort(String(port));

        // Load env vars - 支持 value 和 valueFrom 两种类型
        const currentEnvs = container.env?.map(e => ({
          name: e.name,
          value: e.value || '',
          valueFrom: e.valueFrom,
        })) || [];
        setEnvVars(currentEnvs);

        // Load resources - 分别加载 limits 和 requests
        setCpuLimit(container.resources?.limits?.cpu || '');
        setMemoryLimit(container.resources?.limits?.memory || '');
        setCpuRequest(container.resources?.requests?.cpu || '');
        setMemoryRequest(container.resources?.requests?.memory || '');
      }

      // Load autoscaling annotations
      // 注意：Knative 的 autoscaling 注解可能在 template.metadata.annotations 中
      const templateAnnotations = service.spec.template?.metadata?.annotations || {};
      // 也可能在 service.metadata.annotations 中（某些版本）
      const serviceAnnotations = service.metadata?.annotations || {};

      // Knative 使用驼峰式注解：autoscaling.knative.dev/minScale 和 autoscaling.knative.dev/maxScale
      // 但也可能使用短横线格式：autoscaling.knative.dev/min-scale 和 autoscaling.knative.dev/max-scale
      // 优先从 template annotations 读取，如果没有则从 service annotations 读取
      const minScaleValue = templateAnnotations['autoscaling.knative.dev/minScale'] ||
        templateAnnotations['autoscaling.knative.dev/min-scale'] ||
        serviceAnnotations['autoscaling.knative.dev/minScale'] ||
        serviceAnnotations['autoscaling.knative.dev/min-scale'] ||
        '';
      const maxScaleValue = templateAnnotations['autoscaling.knative.dev/maxScale'] ||
        templateAnnotations['autoscaling.knative.dev/max-scale'] ||
        serviceAnnotations['autoscaling.knative.dev/maxScale'] ||
        serviceAnnotations['autoscaling.knative.dev/max-scale'] ||
        '';

      setMinScale(minScaleValue);
      setMaxScale(maxScaleValue);

      // 调试日志（开发环境）
      if (process.env.NODE_ENV === 'development') {
        console.log('[CreateRevisionDialog] Loaded autoscaling:', {
          minScale: minScaleValue,
          maxScale: maxScaleValue,
          templateAnnotations: Object.keys(templateAnnotations).filter(k => k.includes('autoscaling')),
          serviceAnnotations: Object.keys(serviceAnnotations).filter(k => k.includes('autoscaling')),
          allTemplateAnnotations: templateAnnotations,
        });
      }

      // Load concurrency
      setConcurrency(String(service.spec.template?.spec?.containerConcurrency || 0));
    }

    setActiveStep(0);
    setTrafficStrategy('no-traffic'); // 默认选择 0% 流量
    setCreateError(null);
    setCreateSuccess(false);
  };

  // Handle dialog open
  const handleEnter = () => {
    initializeForm();
  };

  // Add environment variable
  const handleAddEnvVar = () => {
    setEnvVars([...envVars, { name: '', value: '' }]);
  };

  // Remove environment variable
  const handleRemoveEnvVar = (index: number) => {
    setEnvVars(envVars.filter((_, i) => i !== index));
  };

  // Update environment variable
  const handleEnvVarChange = (index: number, field: 'name' | 'value', value: string) => {
    const updated = [...envVars];
    updated[index] = { ...updated[index], [field]: value };
    setEnvVars(updated);
  };

  // Validate step 1
  const isStep1Valid = () => {
    return imageRegistry.trim() !== '' && imageTag.trim() !== '' && containerPort.trim() !== '';
  };

  // Validate step 2 - now always valid since we use predefined strategies
  const isStep2Valid = () => {
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (activeStep === STEPS.length - 1) {
      onSuccess();
      onClose();
    } else if (activeStep === 1) {
      // Create revision
      handleCreateRevision();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  // Handle back
  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  // Create revision
  // 对齐 kn CLI 的实现模式：GET → DeepCopy → 原地修改用户变更字段 → PUT（完整替换）
  // 参考：knative/client/pkg/serving/v1/client.go UpdateServiceWithRetry
  //
  // 为什么用 PUT 而不是 PATCH：
  // 1. Knative CRD 不支持 Strategic Merge Patch，只支持 JSON Merge Patch
  // 2. JSON Merge Patch 会整体替换数组（containers、env、volumes 等），导致字段丢失
  // 3. PUT + DeepCopy 天然保留所有字段（kn CLI 的实际做法），不需要手动维护 MustKeepFields 清单
  // 4. PUT 自带 resourceVersion 乐观锁，可检测并发冲突
  const handleCreateRevision = async () => {
    if (!service) return;

    setIsCreating(true);
    setCreateError(null);

    try {
      // 获取当前用户信息
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef || 'unknown';
      const timestamp = new Date().toISOString();

      // ========== 步骤 1: DeepCopy 当前 Service（对齐 kn 的 service.DeepCopy()）==========
      // structuredClone 深拷贝整个 Service 对象，所有字段天然保留
      // 不需要手动列举 MustKeepFields，不会遗漏任何字段
      const updated = structuredClone(service) as any;

      // ========== 步骤 2: 原地修改用户变更的字段（对齐 kn 的 updateFunc）==========
      const container = updated.spec.template?.spec?.containers?.[0];
      if (!container) {
        throw new Error('Service 模板中没有容器定义');
      }

      // 镜像
      container.image = `${imageRegistry}:${imageTag}`;

      // 端口
      const portNum = parseInt(containerPort, 10);
      if (portNum > 0) {
        container.ports = [{ containerPort: portNum }];
      }

      // 环境变量（用户在 UI 上编辑的完整列表）
      const userEnvVars = envVars
        .filter(e => e.name.trim())
        .map(e => {
          if (e.valueFrom) {
            return { name: e.name, valueFrom: e.valueFrom };
          }
          return { name: e.name, value: e.value };
        });
      if (userEnvVars.length > 0) {
        container.env = userEnvVars;
      }

      // 资源限制
      if (cpuRequest || memoryRequest || cpuLimit || memoryLimit) {
        if (!container.resources) container.resources = {};
        if (cpuRequest || memoryRequest) {
          if (!container.resources.requests) container.resources.requests = {};
          if (cpuRequest) container.resources.requests.cpu = cpuRequest;
          if (memoryRequest) container.resources.requests.memory = memoryRequest;
        }
        if (cpuLimit || memoryLimit) {
          if (!container.resources.limits) container.resources.limits = {};
          if (cpuLimit) container.resources.limits.cpu = cpuLimit;
          if (memoryLimit) container.resources.limits.memory = memoryLimit;
        }
      }

      // 命令和参数
      if (command.trim()) {
        container.command = command.split(' ').filter(s => s);
      }
      if (args.trim()) {
        container.args = args.split(' ').filter(s => s);
      }

      // 并发数
      const concurrencyNum = parseInt(concurrency, 10);
      if (!isNaN(concurrencyNum)) {
        updated.spec.template.spec.containerConcurrency = concurrencyNum;
      }

      // ========== 步骤 3: 更新模板注解（原地修改）==========
      if (!updated.spec.template.metadata) updated.spec.template.metadata = {};
      if (!updated.spec.template.metadata.annotations) updated.spec.template.metadata.annotations = {};

      const tplAnnotations = updated.spec.template.metadata.annotations;
      tplAnnotations['backstage.io/created-by'] = userEntityRef;
      tplAnnotations['backstage.io/created-at'] = timestamp;
      if (minScale) tplAnnotations['autoscaling.knative.dev/minScale'] = minScale;
      if (maxScale) tplAnnotations['autoscaling.knative.dev/maxScale'] = maxScale;

      // 清除 revision template name，让 Knative 自动生成新的 revision 名称
      if (updated.spec.template.metadata.name) {
        delete updated.spec.template.metadata.name;
      }

      // ========== 步骤 4: Traffic 保持不变 ==========
      // 新建 Revision 时不修改流量分配，deepCopy 已保留原始 traffic
      // 新 Revision 不在 traffic 列表中，自然得到 0% 流量

      // ========== 步骤 5: 更新 Service 级别的审计注解 ==========
      if (!updated.metadata.annotations) updated.metadata.annotations = {};
      updated.metadata.annotations[BACKSTAGE_MODIFIED_BY] = userEntityRef;
      updated.metadata.annotations[BACKSTAGE_MODIFIED_AT] = timestamp;
      updated.metadata.annotations[BACKSTAGE_MODIFIED_ACTION] = `create-revision-${trafficStrategy}`;

      // ========== 步骤 6: 清理 PUT 不需要的字段 ==========
      // status 是只读的，API Server 会忽略但最好不发
      delete updated.status;
      // managedFields 由 API Server 管理
      delete updated.metadata.managedFields;

      // ========== 步骤 7: PUT 完整对象（对齐 kn 的 UpdateService）==========
      // PUT 使用 resourceVersion 做乐观锁，API Server 会自动检测并发冲突
      // 如果其他人同时修改了 Service，会返回 409 Conflict
      const putServiceUpdate = async (servicePayload: any): Promise<Response> => {
        return kubernetesApi.proxy({
          clusterName,
          path: `/apis/serving.knative.dev/v1/namespaces/${namespace}/services/${service.metadata.name}`,
          init: {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(servicePayload),
          },
        });
      };

      let response = await putServiceUpdate(updated);

      // ========== 冲突重试（对齐 kn 的 RetryOnConflict）==========
      // 如果返回 409 Conflict，说明 resourceVersion 已过期
      // 重新 GET 最新的 Service → 重新应用用户变更 → 重新 PUT
      if (response.status === 409) {
        console.warn('[CreateRevisionDialog] 检测到版本冲突，正在重试...');

        // 重新获取最新的 Service
        const refreshResponse = await kubernetesApi.proxy({
          clusterName,
          path: `/apis/serving.knative.dev/v1/namespaces/${namespace}/services/${service.metadata.name}`,
        });

        if (!refreshResponse.ok) {
          throw new Error(`刷新 Service 失败: ${refreshResponse.statusText}`);
        }

        const freshService = await refreshResponse.json();
        // 用最新的 resourceVersion 更新 payload
        updated.metadata.resourceVersion = freshService.metadata.resourceVersion;

        response = await putServiceUpdate(updated);
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`创建失败: ${response.statusText} - ${errorText}`);
      }

      setCreateSuccess(true);
      setActiveStep(2);
    } catch (err) {
      console.error('Failed to create revision:', err);
      setCreateError(err instanceof Error ? err.message : '创建修订版本失败');
    } finally {
      setIsCreating(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderTrafficConfig();
      case 2:
        return renderCompletion();
      default:
        return null;
    }
  };

  // Step 1: Basic info form
  const renderBasicInfo = () => (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="镜像名称"
            value={imageRegistry}
            onChange={e => setImageRegistry(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="如: registry-prod.example.com/prod/my-service"
            className={classes.formField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="镜像版本"
            value={imageTag}
            onChange={e => setImageTag(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="如: 2.9.0"
            className={classes.formField}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset" className={classes.formField}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              访问协议
            </Typography>
            <RadioGroup
              value={protocol}
              onChange={e => setProtocol(e.target.value as 'http' | 'grpc')}
              className={classes.protocolGroup}
            >
              <FormControlLabel value="http" control={<Radio color="primary" />} label="HTTP" />
              <FormControlLabel value="grpc" control={<Radio color="primary" />} label="gRPC" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="容器端口"
            value={containerPort}
            onChange={e => setContainerPort(e.target.value)}
            type="number"
            variant="outlined"
            className={classes.formField}
          />
        </Grid>
      </Grid>

      {/* Advanced Settings */}
      <Accordion className={classes.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>高级设置</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="请求最大并发数"
                  value={concurrency}
                  onChange={e => setConcurrency(e.target.value)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  helperText="0 表示不限制"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="最小缩容实例数"
                  value={minScale}
                  onChange={e => setMinScale(e.target.value)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  helperText="0 表示可缩容到零"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="最大扩容实例数"
                  value={maxScale}
                  onChange={e => setMaxScale(e.target.value)}
                  type="number"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
              资源配置
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary" gutterBottom style={{ display: 'block' }}>
                  资源请求 (Requests)
                </Typography>
                <Box className={classes.resourceRow}>
                  <TextField
                    label="CPU"
                    value={cpuRequest}
                    onChange={e => setCpuRequest(e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="如: 0.5"
                    className={classes.inputSmall}
                  />
                  <TextField
                    label="内存"
                    value={memoryRequest}
                    onChange={e => setMemoryRequest(e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="如: 512Mi"
                    className={classes.inputSmall}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary" gutterBottom style={{ display: 'block' }}>
                  资源限制 (Limits)
                </Typography>
                <Box className={classes.resourceRow}>
                  <TextField
                    label="CPU"
                    value={cpuLimit}
                    onChange={e => setCpuLimit(e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="如: 1"
                    className={classes.inputSmall}
                  />
                  <TextField
                    label="内存"
                    value={memoryLimit}
                    onChange={e => setMemoryLimit(e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="如: 1Gi"
                    className={classes.inputSmall}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box style={{ marginTop: 16, marginBottom: 8 }}>
              <Typography variant="subtitle2">生命周期</Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                容器的启动命令和参数，用于覆盖镜像默认的 ENTRYPOINT 和 CMD
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="命令 (Command)"
                  value={command}
                  onChange={e => setCommand(e.target.value)}
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="如: /bin/sh, python, node"
                  helperText="覆盖镜像的 ENTRYPOINT"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="参数 (Args)"
                  value={args}
                  onChange={e => setArgs(e.target.value)}
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="如: -c, app.py, server.js"
                  helperText="覆盖镜像的 CMD"
                />
              </Grid>
            </Grid>

            {/* Environment Variables */}
            <Box className={classes.envHeader} style={{ marginTop: 16 }}>
              <Typography variant="subtitle2">环境变量</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddEnvVar}
                color="primary"
              >
                新增
              </Button>
            </Box>
            {envVars.length > 0 && (
              <TableContainer className={classes.envTable}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>变量名称</TableCell>
                      <TableCell>变量值</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {envVars.map((env, index) => {
                      // 判断是否是 valueFrom 类型的环境变量
                      const isValueFrom = !!env.valueFrom;
                      let valueFromDisplay = '';
                      if (env.valueFrom?.fieldRef) {
                        valueFromDisplay = `$(${env.valueFrom.fieldRef.fieldPath})`;
                      } else if (env.valueFrom?.secretKeyRef) {
                        valueFromDisplay = `Secret: ${env.valueFrom.secretKeyRef.name}/${env.valueFrom.secretKeyRef.key}`;
                      } else if (env.valueFrom?.configMapKeyRef) {
                        valueFromDisplay = `ConfigMap: ${env.valueFrom.configMapKeyRef.name}/${env.valueFrom.configMapKeyRef.key}`;
                      }

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              value={env.name}
                              onChange={e => handleEnvVarChange(index, 'name', e.target.value)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              disabled={isValueFrom}
                            />
                          </TableCell>
                          <TableCell>
                            {isValueFrom ? (
                              <Chip
                                label={valueFromDisplay}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <TextField
                                value={env.value}
                                onChange={e => handleEnvVarChange(index, 'value', e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveEnvVar(index)}
                              disabled={isValueFrom}
                              title={isValueFrom ? '系统环境变量不可删除' : '删除'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  // Step 2: Traffic strategy selection (simplified)
  const renderTrafficConfig = () => (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        选择流量策略
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        创建新修订版本后，如何分配流量？
      </Typography>

      {TRAFFIC_STRATEGIES.map(strategy => {
        // 禁用 10% 和 100% 选项，只允许选择 0%
        const isDisabled = strategy.value !== 'no-traffic';

        return (
          <Box
            key={strategy.value}
            className={`${classes.strategyCard} ${trafficStrategy === strategy.value ? classes.strategyCardSelected : ''}`}
            onClick={() => !isDisabled && setTrafficStrategy(strategy.value)}
            style={{
              opacity: isDisabled ? 0.5 : 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Box className={classes.strategyHeader}>
              <Box display="flex" alignItems="center">
                <Radio
                  checked={trafficStrategy === strategy.value}
                  disabled={isDisabled}
                  color="primary"
                  size="small"
                />
                <Typography variant="subtitle2">{strategy.label}</Typography>
                <Chip
                  size="small"
                  label={strategy.percent === 0 ? '0%' : `${strategy.percent}%`}
                  color={strategy.percent === 100 ? 'primary' : strategy.percent > 0 ? 'secondary' : 'default'}
                  className={classes.strategyChip}
                />
                {isDisabled && (
                  <Chip
                    size="small"
                    label="暂不可用"
                    style={{ marginLeft: 8, backgroundColor: '#f5f5f5', color: '#999' }}
                  />
                )}
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary" style={{ marginLeft: 42 }}>
              {strategy.description}
              {isDisabled && ' (请在流量管理页面调整流量分配)'}
            </Typography>
          </Box>
        );
      })}

      <Box mt={3} p={2} bgcolor="action.hover" borderRadius={1}>
        <Typography variant="body2" color="textSecondary">
          <strong>提示：</strong>
          新版本创建后不会接收流量（0%），当前流量分配保持不变。您可以稍后在"流量分配"卡片中手动调整流量比例。
        </Typography>
      </Box>

      {createError && (
        <Box mt={2}>
          <Typography variant="body2" color="error">
            {createError}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Step 3: Completion
  const renderCompletion = () => {
    const selectedStrategy = TRAFFIC_STRATEGIES.find(s => s.value === trafficStrategy);

    return (
      <Box className={classes.completionBox}>
        {createSuccess ? (
          <>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              创建修订版本已提交
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {trafficStrategy === 'no-traffic' && '新版本已创建，但暂未分配流量。您可以在"流量分配"卡片中手动切换。'}
              {trafficStrategy === 'canary' && `新版本已创建，${selectedStrategy?.percent}% 流量已切换到新版本。`}
              {trafficStrategy === 'full' && '新版本已创建，100% 流量已切换到新版本。'}
            </Typography>
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                镜像: {imageRegistry}:{imageTag}
              </Typography>
            </Box>
          </>
        ) : createError ? (
          <>
            <ErrorIcon className={classes.errorIcon} />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              创建失败
            </Typography>
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              {createError}
            </Typography>
          </>
        ) : null}
      </Box>
    );
  };

  // Get button text
  const getNextButtonText = () => {
    if (activeStep === 0) return '下一步';
    if (activeStep === 1) return isCreating ? '创建中...' : '创建';
    return '关闭';
  };

  // Check if next button should be disabled
  const isNextDisabled = () => {
    if (activeStep === 0) return !isStep1Valid();
    if (activeStep === 1) return !isStep2Valid() || isCreating;
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle>创建修订版本</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          此功能验证中 — 新版本创建后，请先查看 YAML 并与上一版本对比，确认无误后逐步切换流量，验证通过再全量切换。
        </Alert>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {isCreating && <LinearProgress />}

        {renderStepContent()}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && activeStep < 2 && (
          <Button onClick={handleBack} disabled={isCreating}>
            上一步
          </Button>
        )}
        {activeStep < 2 && (
          <Button onClick={onClose} disabled={isCreating}>
            取消
          </Button>
        )}
        <Button
          onClick={handleNext}
          color="primary"
          variant="contained"
          disabled={isNextDisabled()}
        >
          {getNextButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

